import { useState, useMemo } from 'react';
import { useQueries } from 'react-query';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  getAsmVulnerabilityPaged,
  getSeverityList,
  getVulnerabilityCounter,
  getVulnerabilityList,
} from 'services/asm-service';
import { useAuthTenant } from 'store/auth';
import { DefaultPagination } from 'Components/common/DefaultPagination';
import { Shimmer } from 'Components/PageLoader/styles';
import { PageLoaderList } from 'Components/PageLoader/List';
import styled from 'styled-components';

const FlexRow = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: row;
  margin-block: 16px;
  margin-inline: auto;
`;

const Card = styled.div`
  background: white;
  border-radius: 14px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  padding: 20px;
`;

const CardTitle = styled.div`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: var(--vo-ink);
  margin-bottom: 16px;
`;

const TH_STYLE = {
  fontFamily: 'Maven Pro, sans-serif',
  fontSize: '11px',
  fontWeight: 600,
  color: 'var(--vo-slate)',
  textTransform: 'uppercase',
  letterSpacing: '.08em',
  padding: '11px 14px',
  textAlign: 'left',
  whiteSpace: 'nowrap',
};

const TD_STYLE = {
  padding: '12px 14px',
  fontFamily: 'Lato, sans-serif',
  fontSize: '13px',
  color: 'var(--vo-ink)',
  verticalAlign: 'middle',
};

const SEVERITY_NAMED = [
  { key: 'critical', color: 'var(--vo-danger)' },
  { key: 'high', color: 'var(--vo-warning)' },
  { key: 'medium', color: 'var(--vo-sev-medium)' },
  { key: 'low', color: 'var(--vo-success)' },
  { key: 'info', color: 'var(--vo-capri)' },
];
const DONUT_FALLBACK = ['var(--vo-danger)', 'var(--vo-warning)', 'var(--vo-sev-medium)', 'var(--vo-success)', 'var(--vo-capri)', 'var(--vo-steel)'];

function getSeverityColor(name) {
  const k = (name || '').toLowerCase();
  const found = SEVERITY_NAMED.find(s => k.includes(s.key));
  return found ? found.color : null;
}

const SEVERITY_BADGE = {
  critical: { bg: 'var(--vo-danger-10)', color: 'var(--vo-danger)' },
  high: { bg: 'var(--vo-warning-10)', color: 'var(--vo-warning)' },
  medium: { bg: 'var(--vo-warning-10)', color: 'var(--vo-sev-medium)' },
  low: { bg: 'var(--vo-success-10)', color: 'var(--vo-success)' },
  info: { bg: 'var(--vo-capri-50)', color: 'var(--vo-capri)' },
};

const STATUS_BADGE = {
  open: { bg: 'var(--vo-danger-10)', color: 'var(--vo-danger)' },
  closed: { bg: 'var(--vo-success-10)', color: 'var(--vo-success)' },
  resolved: { bg: 'var(--vo-success-10)', color: 'var(--vo-success)' },
  in_progress: { bg: 'var(--vo-warning-10)', color: 'var(--vo-warning)' },
};

function getBadgeStyle(map, key) {
  const k = (key || '').toLowerCase().replace(/[-\s]/g, '_');
  return map[k] || { bg: 'var(--vo-mist)', color: 'var(--vo-slate)' };
}

function Badge({ label, bg, color }) {
  return (
    <span style={{ background: bg, color, borderRadius: '6px', padding: '3px 8px', fontSize: '11px', fontFamily: 'Maven Pro, sans-serif', fontWeight: 600, display: 'inline-block' }}>
      {label}
    </span>
  );
}

function HBar({ label, qt, maxQt, color }) {
  const pct = maxQt > 0 ? Math.max(2, (qt / maxQt) * 100) : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
      <span style={{ width: '90px', fontSize: '12px', fontFamily: 'Maven Pro, sans-serif', color: 'var(--vo-slate)', flexShrink: 0, textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {label}
      </span>
      <div style={{ flex: 1, background: 'var(--vo-mist)', borderRadius: '4px', height: '20px', overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, background: color, height: '100%', borderRadius: '3px' }} />
      </div>
      <span style={{ fontSize: '12px', fontFamily: 'Maven Pro, sans-serif', fontWeight: 600, color: color, minWidth: '24px' }}>{qt}</span>
    </div>
  );
}

function polarToCartesian(cx, cy, r, deg) {
  const rad = (deg - 90) * Math.PI / 180;
  return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
}

function donutArcPath(cx, cy, r1, r2, startDeg, endDeg) {
  const [x1, y1] = polarToCartesian(cx, cy, r2, startDeg);
  const [x2, y2] = polarToCartesian(cx, cy, r2, endDeg);
  const [x3, y3] = polarToCartesian(cx, cy, r1, endDeg);
  const [x4, y4] = polarToCartesian(cx, cy, r1, startDeg);
  const large = endDeg - startDeg > 180 ? 1 : 0;
  return `M${x1.toFixed(2)},${y1.toFixed(2)} A${r2},${r2} 0 ${large} 1 ${x2.toFixed(2)},${y2.toFixed(2)} L${x3.toFixed(2)},${y3.toFixed(2)} A${r1},${r1} 0 ${large} 0 ${x4.toFixed(2)},${y4.toFixed(2)} Z`;
}

function SvgDonut({ data }) {
  const total = (data || []).reduce((s, d) => s + (d.qt || 0), 0);
  if (!total) {
    return (
      <div style={{ height: '120px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--vo-steel)', fontFamily: 'Lato, sans-serif', fontSize: '13px' }}>
        —
      </div>
    );
  }

  let angle = 0;
  const segments = (data || []).map((d, i) => {
    const start = angle;
    const sweep = Math.min((d.qt / total) * 360, 359.99);
    angle += (d.qt / total) * 360;
    return {
      name: d.name,
      qt: d.qt,
      start,
      sweep,
      color: getSeverityColor(d.name) || DONUT_FALLBACK[i % DONUT_FALLBACK.length],
    };
  });

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
      <svg width="120" height="120" viewBox="0 0 120 120" style={{ flexShrink: 0 }}>
        {segments.map((seg, i) => (
          <path key={i} d={donutArcPath(60, 60, 32, 55, seg.start, seg.start + seg.sweep)} fill={seg.color} />
        ))}
        <circle cx="60" cy="60" r="24" fill="white" />
        <text x="60" y="60" textAnchor="middle" dominantBaseline="middle" style={{ fontSize: '16px', fontWeight: 700, fontFamily: 'Rubik, sans-serif', fill: 'var(--vo-ink)' }}>{total}</text>
      </svg>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', flex: 1, minWidth: '100px' }}>
        {segments.map((seg, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ width: '8px', height: '8px', borderRadius: '2px', background: seg.color, flexShrink: 0, display: 'inline-block' }} />
            <span style={{ fontSize: '12px', fontFamily: 'Lato, sans-serif', color: 'var(--vo-slate)', flex: 1 }}>{seg.name}</span>
            <span style={{ fontSize: '12px', fontFamily: 'Lato, sans-serif', color: 'var(--vo-ink)', fontWeight: 600 }}>{seg.qt}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export const TabVulnerability = () => {
  const { t } = useTranslation(['asm']);
  const [page, setPage] = useState(1);
  const selectedTenant = useSelector(useAuthTenant);

  const queries = useQueries([
    {
      queryKey: ['getVulnerabilityCounter'],
      queryFn: async () => {
        if (selectedTenant.slug === undefined) return null;
        const response = await getVulnerabilityCounter(selectedTenant.slug);
        if (response?.data && response?.data?.vulnerabilityCounter) return response.data.vulnerabilityCounter;
        return 0;
      },
    },
    {
      queryKey: ['getSeverityList'],
      queryFn: async () => {
        if (selectedTenant.slug === undefined) return null;
        const response = await getSeverityList(selectedTenant.slug);
        if (response?.data?.severities) return response.data.severities;
        return [];
      },
    },
    {
      queryKey: ['getVulnerabilityList'],
      queryFn: async () => {
        if (selectedTenant.slug === undefined) return null;
        const response = await getVulnerabilityList(selectedTenant.slug);
        if (response?.data?.vulnerabilities) return response.data.vulnerabilities;
        return [];
      },
    },
    {
      queryKey: ['getAsmVulnerabilityPaged', page],
      queryFn: async () => {
        if (selectedTenant.slug === undefined) return [];
        const result = await getAsmVulnerabilityPaged(selectedTenant.slug, page, 6);
        if (result?.data?.data.length > 0) return result.data;
        return { data: [], page, totalPages: 0 };
      },
    },
  ]);

  const [
    { data: vulnerabilityCounter, isLoading: vulnerabilityCounterLoading },
    { data: severities, isLoading: severityLoading },
    { data: vulnerabilities, isLoading: vulnerabilityLoading },
    { data: pagedAsmVulnerability, isLoading: asmVulnerabilityLoading },
  ] = queries;

  const vulnTypeData = useMemo(() => {
    if (!vulnerabilities?.length) return [];
    const items = vulnerabilities[0]?.qt !== undefined
      ? vulnerabilities
      : vulnerabilities.map(v => ({ name: v.title || v.name || 'Unknown', qt: 1 }));
    return [...items].sort((a, b) => b.qt - a.qt).slice(0, 5);
  }, [vulnerabilities]);

  const vulnMax = vulnTypeData.reduce((m, d) => Math.max(m, d.qt || 0), 0);

  const columns = [
    { id: 'title', label: t('asm:tabVulnerability.table.title') },
    { id: 'host', label: t('asm:tabVulnerability.table.host') },
    { id: 'description', label: t('asm:tabVulnerability.table.description') },
    { id: 'severity', label: t('asm:tabVulnerability.table.severity') },
    { id: 'status', label: t('asm:tabVulnerability.table.status') },
  ];

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'row', gap: '24px', marginBottom: '24px', alignItems: 'stretch', flexWrap: 'wrap' }} data-tour="vuln-overview">
        <div
          style={{ width: '200px', flexShrink: 0, background: 'white', borderRadius: '14px', boxShadow: '0 1px 3px var(--vo-escuro-8)', padding: '24px', display: 'flex', flexDirection: 'column', gap: '8px', justifyContent: 'center' }}
          data-tour="vuln-counter"
        >
          <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'var(--vo-danger-10)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--vo-danger)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </div>
          {vulnerabilityCounterLoading
            ? <Shimmer style={{ height: '44px', width: '80px' }} />
            : <div style={{ fontFamily: 'Rubik, sans-serif', fontSize: '44px', fontWeight: 700, color: 'var(--vo-danger)', lineHeight: 1 }}>{vulnerabilityCounter ?? 0}</div>
          }
          <div style={{ fontFamily: 'Maven Pro, sans-serif', fontSize: '11px', fontWeight: 600, color: 'var(--vo-slate)', textTransform: 'uppercase', letterSpacing: '.08em' }}>
            {t('asm:tabVulnerability.cardTitle')}
          </div>
        </div>
        <Card style={{ width: '280px', flexShrink: 0 }} data-tour="severity-chart">
          <CardTitle>{t('asm:tabVulnerability.chartSeverities')}</CardTitle>
          {severityLoading
            ? <Shimmer style={{ height: '120px', width: '100%' }} />
            : <SvgDonut data={severities} />
          }
        </Card>
        <Card style={{ flex: 1, minWidth: '280px' }} data-tour="vuln-chart">
          <CardTitle>{t('asm:tabVulnerability.chartTop5')}</CardTitle>
          {vulnerabilityLoading
            ? <Shimmer style={{ height: '150px', width: '100%' }} />
            : vulnTypeData.map(d => (
                <HBar
                  key={d.name}
                  label={d.name}
                  qt={d.qt}
                  maxQt={vulnMax}
                  color={getSeverityColor(d.name) || 'var(--vo-celeste)'}
                />
              ))
          }
        </Card>
      </div>
      {asmVulnerabilityLoading && <PageLoaderList />}
      {!asmVulnerabilityLoading && (
        <>
          <div style={{ background: 'white', borderRadius: '14px', boxShadow: '0 1px 3px var(--vo-escuro-8)', overflow: 'auto' }} data-tour="vuln-table">
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }}>
              <thead>
                <tr style={{ background: 'var(--vo-mist)' }}>
                  {columns.map(col => (
                    <th key={col.id} style={TH_STYLE}>{col.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(pagedAsmVulnerability?.data || []).length === 0 && (
                  <tr>
                    <td colSpan={columns.length} style={{ ...TD_STYLE, textAlign: 'center', color: 'var(--vo-steel)', padding: '24px' }}>—</td>
                  </tr>
                )}
                {(pagedAsmVulnerability?.data || []).map((row, i) => {
                  const sevStyle = getBadgeStyle(SEVERITY_BADGE, row.severity);
                  const stsStyle = getBadgeStyle(STATUS_BADGE, row.status);
                  return (
                    <tr key={i} style={{ borderBottom: '1px solid var(--vo-mist)' }}>
                      <td style={{ ...TD_STYLE, fontWeight: 500, maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.title ?? '—'}</td>
                      <td style={{ ...TD_STYLE, fontFamily: 'Maven Pro, sans-serif', fontSize: '12px', fontWeight: 600, color: 'var(--color-brand)' }}>{row.host ?? '—'}</td>
                      <td style={{ ...TD_STYLE, fontFamily: 'Lato, sans-serif', fontSize: '12px', color: 'var(--vo-slate)', maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.description ?? '—'}</td>
                      <td style={{ ...TD_STYLE, textAlign: 'center' }}>
                        {row.severity ? <Badge label={row.severity} bg={sevStyle.bg} color={sevStyle.color} /> : '—'}
                      </td>
                      <td style={{ ...TD_STYLE, textAlign: 'center' }}>
                        {row.status ? <Badge label={row.status} bg={stsStyle.bg} color={stsStyle.color} /> : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {pagedAsmVulnerability?.totalPages > 0 && (
            <FlexRow data-tour="vuln-pagination">
              <DefaultPagination
                count={pagedAsmVulnerability?.totalPages || 0}
                onChange={(_, selectedPage) => setPage(selectedPage)}
                color="primary"
                page={page}
                showFirstButton
                showLastButton
              />
            </FlexRow>
          )}
        </>
      )}
    </>
  );
};
